'use client';

import { Dialog, Transition } from '@headlessui/react';
import { UserIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useState, useRef, useEffect } from 'react';
import { useCustomer } from './customer-context';
import clsx from 'clsx';
import Link from 'next/link';
import UserMenu from './user-menu';
import { motion, AnimatePresence } from 'framer-motion';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

// This is the actual modal component that uses reCAPTCHA
function AccountModalContent() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isRecovering, setIsRecovering] = useState(false);
  
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { customer, isAuthenticated, isLoading, login, logout, signup, forgotPassword } = useCustomer();

  // Check if passwords match when they change
  useEffect(() => {
    if (signupPassword && signupConfirmPassword) {
      setPasswordsMatch(signupPassword === signupConfirmPassword);
    } else {
      setPasswordsMatch(true); // No validation when fields are empty
    }
  }, [signupPassword, signupConfirmPassword]);

  // If user is authenticated, render the UserMenu component
  if (isAuthenticated && customer) {
    return <UserMenu />;
  }

  // Form handlers
  const openModal = () => {
    setIsOpen(true);
    setError('');
  };

  const closeModal = () => {
    setIsOpen(false);
    setError('');
    // Reset form fields
    setLoginEmail('');
    setLoginPassword('');
    setSignupFirstName('');
    setSignupLastName('');
    setSignupEmail('');
    setSignupPassword('');
    setSignupConfirmPassword('');
    setForgotPasswordEmail('');
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setError('');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not available');
      }
      
      // Execute reCAPTCHA with login action
      const token = await executeRecaptcha('login');
      
      await login(loginEmail, loginPassword, token);
      closeModal();
    } catch (error) {
      // Set a more user-friendly error message
      if (error instanceof Error) {
        if (error.message.includes('customer') && error.message.includes('not found')) {
          setError('Invalid email or password. Please try again.');
        } else if (error.message.includes('reCAPTCHA')) {
          setError('Security verification failed. Please try again.');
        } else {
          setError(error.message);
        }
      } else {
        setError('Unable to login. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (signupPassword !== signupConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not available');
      }
      
      // Execute reCAPTCHA with signup action
      const token = await executeRecaptcha('signup');
      
      await signup(signupFirstName, signupLastName, signupEmail, signupPassword, token);
      closeModal();
    } catch (error) {
      // Set a more user-friendly error message
      if (error instanceof Error) {
        if (error.message.includes('reCAPTCHA')) {
          setError('Security verification failed. Please try again.');
        } else {
          setError(error.message);
        }
      } else {
        setError('Unable to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsRecovering(true);
    
    // Use the login email if available, otherwise use the dedicated forgotPasswordEmail
    const emailToRecover = loginEmail || forgotPasswordEmail;
    
    if (!emailToRecover) {
      setError('Please enter your email address');
      setIsRecovering(false);
      return;
    }
    
    try {
      await forgotPassword(emailToRecover);
      // We don't need to show a success message here as it's handled in the forgotPassword function
      closeModal();
    } catch (error) {
      // We don't show errors for password recovery to prevent email enumeration
    } finally {
      setIsRecovering(false);
    }
  };

  const handleLogout = async () => {
    setIsSubmitting(true);
    try {
      await logout();
      closeModal();
    } catch (error) {
      // Error is handled in the logout function
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        aria-label="Open account menu"
        onClick={openModal}
        className="relative hidden md:flex h-11 items-center justify-center rounded-md border border-neutral-700 bg-neutral-900 px-4 text-white transition-colors hover:bg-neutral-800 gap-2"
      >
        <UserIcon className="h-5 w-5" />
        <span className="text-sm font-medium">Login/Register</span>
      </button>

      <Transition show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/90" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-md transform rounded-xl bg-[#1A1A1D] p-4 text-left align-middle shadow-xl transition-all overflow-hidden">
                  <button
                    onClick={closeModal}
                    className="absolute right-4 top-4 text-neutral-500 hover:text-white"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>

                  {/* Tab selection */}
                  <div className="flex border-b border-neutral-800">
                    <button
                      className={clsx(
                        'flex-1 py-3 text-center text-sm font-medium',
                        activeTab === 'login'
                          ? 'border-b-2 border-white text-white'
                          : 'text-neutral-400 hover:text-neutral-300'
                      )}
                      onClick={() => handleTabChange('login')}
                    >
                      Login
                    </button>
                    <button
                      className={clsx(
                        'flex-1 py-3 text-center text-sm font-medium',
                        activeTab === 'signup'
                          ? 'border-b-2 border-white text-white'
                          : 'text-neutral-400 hover:text-neutral-300'
                      )}
                      onClick={() => handleTabChange('signup')}
                    >
                      Create Account
                    </button>
                  </div>

                  {/* Error message */}
                  {error && (
                    <div className="mt-4 text-center text-sm text-red-500">{error}</div>
                  )}

                  <div className="py-4">
                    <AnimatePresence mode="wait">
                      {/* Login Form */}
                      {activeTab === 'login' && (
                        <motion.div
                          key="login"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.2 }}
                        >
                          <form onSubmit={handleLogin}>
                            <div className="space-y-4">
                              <div>
                                <label htmlFor="email" className="block text-sm font-medium text-neutral-300">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  id="email"
                                  name="email"
                                  value={loginEmail}
                                  onChange={(e) => setLoginEmail(e.target.value)}
                                  required
                                  className="mt-1 block w-full rounded-md bg-neutral-800 border border-neutral-700 py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-neutral-500 focus:border-neutral-500"
                                />
                              </div>
                              <div>
                                <label htmlFor="password" className="block text-sm font-medium text-neutral-300">
                                  Password
                                </label>
                                <input
                                  type="password"
                                  id="password"
                                  name="password"
                                  value={loginPassword}
                                  onChange={(e) => setLoginPassword(e.target.value)}
                                  required
                                  className="mt-1 block w-full rounded-md bg-neutral-800 border border-neutral-700 py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-neutral-500 focus:border-neutral-500"
                                />
                              </div>
                              <div className="text-right">
                                <button
                                  type="button"
                                  onClick={handleForgotPassword}
                                  disabled={isRecovering}
                                  className="text-xs text-neutral-400 hover:text-white focus:outline-none disabled:opacity-50"
                                >
                                  {isRecovering ? 'Sending recovery email...' : 'Forgot password?'}
                                </button>
                              </div>
                            </div>

                            <div className="pt-2">
                              <button
                                type="submit"
                                disabled={isSubmitting || !loginEmail || !loginPassword}
                                className="w-full rounded-md bg-neutral-100 px-4 py-3 text-center font-medium text-neutral-900 hover:bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {isSubmitting ? 'Logging in...' : 'Login'}
                              </button>
                            </div>
                            <p className="text-center text-xs text-neutral-500 mt-4">
                              This site is protected by reCAPTCHA and the 
                              Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white">Terms of Service</a> apply.
                            </p>
                          </form>
                        </motion.div>
                      )}

                      {/* Sign Up Form */}
                      {activeTab === 'signup' && (
                        <motion.div
                          key="signup"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.2 }}
                        >
                          <form onSubmit={handleSignup}>
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label htmlFor="firstName" className="block text-sm font-medium text-neutral-300">
                                    First Name
                                  </label>
                                  <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={signupFirstName}
                                    onChange={(e) => setSignupFirstName(e.target.value)}
                                    required
                                    className="mt-1 block w-full rounded-md bg-neutral-800 border border-neutral-700 py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-neutral-500 focus:border-neutral-500"
                                  />
                                </div>
                                <div>
                                  <label htmlFor="lastName" className="block text-sm font-medium text-neutral-300">
                                    Last Name
                                  </label>
                                  <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={signupLastName}
                                    onChange={(e) => setSignupLastName(e.target.value)}
                                    required
                                    className="mt-1 block w-full rounded-md bg-neutral-800 border border-neutral-700 py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-neutral-500 focus:border-neutral-500"
                                  />
                                </div>
                              </div>
                              <div>
                                <label htmlFor="signupEmail" className="block text-sm font-medium text-neutral-300">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  id="signupEmail"
                                  name="email"
                                  value={signupEmail}
                                  onChange={(e) => setSignupEmail(e.target.value)}
                                  required
                                  className="mt-1 block w-full rounded-md bg-neutral-800 border border-neutral-700 py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-neutral-500 focus:border-neutral-500"
                                />
                              </div>
                              <div>
                                <label htmlFor="signupPassword" className="block text-sm font-medium text-neutral-300">
                                  Password
                                </label>
                                <input
                                  type="password"
                                  id="signupPassword"
                                  name="password"
                                  value={signupPassword}
                                  onChange={(e) => setSignupPassword(e.target.value)}
                                  required
                                  minLength={5}
                                  className="mt-1 block w-full rounded-md bg-neutral-800 border border-neutral-700 py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-neutral-500 focus:border-neutral-500"
                                />
                              </div>
                              <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-300">
                                  Confirm Password
                                </label>
                                <input
                                  type="password"
                                  id="confirmPassword"
                                  name="confirmPassword"
                                  value={signupConfirmPassword}
                                  onChange={(e) => setSignupConfirmPassword(e.target.value)}
                                  required
                                  className={`mt-1 block w-full rounded-md bg-neutral-800 border ${
                                    !passwordsMatch && signupConfirmPassword ? 'border-red-500' : 'border-neutral-700'
                                  } py-2 px-3 text-white focus:outline-none focus:ring-1 ${
                                    !passwordsMatch && signupConfirmPassword
                                      ? 'focus:ring-red-500 focus:border-red-500'
                                      : 'focus:ring-neutral-500 focus:border-neutral-500'
                                  }`}
                                />
                                {!passwordsMatch && signupConfirmPassword && (
                                  <p className="mt-1 text-xs text-red-500">Passwords do not match</p>
                                )}
                              </div>
                            </div>

                            <div className="pt-4">
                              <button
                                type="submit"
                                disabled={
                                  isSubmitting ||
                                  !signupFirstName ||
                                  !signupLastName ||
                                  !signupEmail ||
                                  !signupPassword ||
                                  !signupConfirmPassword ||
                                  !passwordsMatch
                                }
                                className="w-full rounded-md bg-neutral-100 px-4 py-3 text-center font-medium text-neutral-900 hover:bg-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {isSubmitting ? 'Creating Account...' : 'Create Account'}
                              </button>
                            </div>
                            <p className="text-center text-xs text-neutral-500 mt-4">
                              This site is protected by reCAPTCHA and the 
                              Google <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white">Terms of Service</a> apply.
                            </p>
                          </form>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}

// This is the main exported component that wraps the modal with ReCaptcha provider
export default function AccountModal() {
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  
  // Only render the ReCaptcha provider on the client side
  if (!isMounted) {
    return (
      <button
        aria-label="Open account menu"
        className="relative hidden md:flex h-11 items-center justify-center rounded-md border border-neutral-700 bg-neutral-900 px-4 text-white transition-colors hover:bg-neutral-800 gap-2"
      >
        <UserIcon className="h-5 w-5" />
        <span className="text-sm font-medium">Login/Register</span>
      </button>
    );
  }
  
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';
  
  if (!siteKey) {
    return <AccountModalContent />;
  }
  
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      <AccountModalContent />
    </GoogleReCaptchaProvider>
  );
}
